import React, { useRef } from 'react';

import { useTranslations } from 'next-intl';

import WalletIcon from '@/assets/icons/wallet-white.svg';
import { P } from '@/components/shared/Typography';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { useCursorPreservation } from '@/hooks/useCursorPreservation';
import { cn } from '@/lib/utils/cn';
import { formatNumberWithCommas } from '@/lib/utils/formatNumberWithCommas';
import { removeCommas } from '@/lib/utils/removeCommas';

interface I_SwapInputProps {
  value: string;
  onChange: (value: string) => void;
  onFocus: () => void;
  onBlur: () => void;
  balance?: string;
  tokenIcon: React.ReactNode;
  tokenLabel: string;
  placeholder?: string;
  hasError?: boolean;
  disabled?: boolean;
  showBalance?: boolean;
  onTokenButtonClick?: () => void;
  isImporting?: boolean;
}

export function SwapInput({
  value,
  onChange,
  onFocus,
  onBlur,
  balance,
  tokenIcon,
  tokenLabel,
  placeholder = '0,00',
  hasError = false,
  disabled = false,
  showBalance = false,
  onTokenButtonClick,
  isImporting = false
}: I_SwapInputProps) {
  const t = useTranslations('pools');
  const inputRef = useRef<HTMLInputElement>(null);
  const { captureCursor } = useCursorPreservation(inputRef, value);

  const handleFocus = () => {
    const cleanedValue = removeCommas(value);
    const numericValue = parseFloat(cleanedValue);

    // If value is 0 (like "0.00" or "0.000000"), clear it so placeholder shows
    if (!isNaN(numericValue) && numericValue === 0 && value) {
      onChange('');
    }

    onFocus();
  };

  // Allow clearing the input when user deletes all content
  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const adjustedValue = captureCursor(e);
    const newValue = adjustedValue ?? e.target.value;
    onChange(newValue);
  };

  return (
    <div className="bg-accordion-brand p-5 rounded-lg">
      <div className="flex justify-between items-start">
        <div className="flex-1 grid gap-3.5">
          <Input
            ref={inputRef}
            value={value}
            onChange={handleChange}
            onFocus={handleFocus}
            onBlur={onBlur}
            placeholder={placeholder}
            className={cn(
              'w-40 outline-none border-0 ring-0! shadow-none p-0 text-primary text-2xl! font-bold leading-loose focus:text-foreground bg-transparent',
              hasError && 'text-destructive'
            )}
            disabled={disabled}
          />
          {showBalance && balance && (
            <P
              icon={<WalletIcon className="w-5 h-5 text-foreground" />}
              className="flex items-center text-foreground text-sm font-semibold"
            >
              {t('balance')}: {formatNumberWithCommas(balance)}
            </P>
          )}
        </div>
        <div className="flex flex-col items-end gap-3.5">
          <Button
            variant="secondary"
            className="flex justify-center items-center gap-2 text-center"
            onClick={onTokenButtonClick}
            disabled={disabled || isImporting}
          >
            {tokenIcon}
            {tokenLabel}
          </Button>
        </div>
      </div>
    </div>
  );
}
